import { Box } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import { type ChangeEvent, type FC, useState } from "react"

import { GoodsReceiptFilterChips, GoodsReceiptFilterDrawer, GoodsReceiptsTable } from "../../../components"
import { useGoodsReceiptsTable } from "../../../hooks"
import type { GoodsReceiptActionState } from "../../../types"
import { GoodReceiptActionType, GoodReceiptDialogType } from "../../../types"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import { PermissionActionType } from "@/constants/permissions"
import { type GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import {
  GoodsReceiptDeleteConfirmation,
  GoodsReceiptPurchaseOrdersTable,
} from "@/screens/WarehouseReceiving/components"
import { PurchaseOrderSelectionWithFormContainer } from "@/screens/WarehouseReceiving/components/PurchaseOrderSelectionDialog/PurchaseOrderSelectionWithFormContainer"

const statuses = [GoodsReceiptStatus.Incoming]

export const IncomingGoodsReceiptsContainer: FC = () => {
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const {
    goodsReceipts,
    isGoodsReceiptsFetching,
    searchQuery,
    warehouseAreaIds,
    warehouseOptions,
    isWarehousesFetching,
    expandedState,
    sortingState,
    tableContainerRef,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleExpandedChange,
    handleSortingChange,
    handleRefetch,
    handleScroll,
  } = useGoodsReceiptsTable({
    statuses,
    routePath: "/app/warehouse-receiving",
  })

  const [actionState, setActionState] = useState<GoodsReceiptActionState>(null)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const handleDialogClose = () => {
    setActionState(null)
  }

  const handleFormDialogSubmit = () => {
    handleDialogClose()
    handleRefetch()
  }

  const handleCreateGoodsReceipt = () => {
    setActionState({ type: GoodReceiptActionType.Form, goodsReceipt: null })
  }

  const handleEditGoodsReceipt = (goodsReceipt: GoodsReceipt) => {
    setActionState({ type: GoodReceiptActionType.Form, goodsReceipt })
  }

  const handleDeleteGoodsReceipt = (goodsReceipt: GoodsReceipt) => {
    setActionState({ type: GoodReceiptActionType.Delete, goodsReceipt })
  }

  const handleDeleteSuccess = () => {
    handleRefetch()
  }

  const handleFilterDrawerToggle = () => {
    setIsFilterDrawerOpen((prev) => !prev)
  }

  const renderExpandedContent = (row: Row<GoodsReceipt>) => (
    <Box className='p-4'>
      <GoodsReceiptPurchaseOrdersTable goodsReceipt={row.original} />
    </Box>
  )

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by AGR, Supplier, Tracking, Container or PO'
          defaultValue={searchQuery}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
        />
        <Button appearance='outlined' startIcon={<FilterAltOutlined />} onClick={handleFilterDrawerToggle}>
          Filter
        </Button>
        {canMaintain && (
          <Button variant='primary' startIcon={<AddOutlined />} className='ml-auto' onClick={handleCreateGoodsReceipt}>
            AGR
          </Button>
        )}
      </Box>
      <GoodsReceiptFilterChips
        warehouseAreaIds={warehouseAreaIds}
        warehouseOptions={warehouseOptions}
        onRemove={handleFilterRemove}
      />
      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <GoodsReceiptsTable
          data={goodsReceipts}
          isLoading={isGoodsReceiptsFetching}
          expandedState={expandedState}
          sortingState={sortingState}
          tableContainerRef={tableContainerRef}
          status={GoodsReceiptStatus.Incoming}
          onExpandedChange={handleExpandedChange}
          onSortingChange={handleSortingChange}
          onEdit={handleEditGoodsReceipt}
          onDelete={handleDeleteGoodsReceipt}
          onScroll={handleScroll}
          renderExpandedContent={renderExpandedContent}
        />
      </Box>
      <GoodsReceiptFilterDrawer
        isOpen={isFilterDrawerOpen}
        warehouseAreaIds={warehouseAreaIds}
        warehouseOptions={warehouseOptions}
        isWarehousesFetching={isWarehousesFetching}
        onToggle={handleFilterDrawerToggle}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />
      {actionState?.type === GoodReceiptActionType.Form && (
        <PurchaseOrderSelectionWithFormContainer
          onClose={handleDialogClose}
          onSubmit={handleFormDialogSubmit}
          {...(actionState.goodsReceipt && {
            dialogType: GoodReceiptDialogType.GoodsReceipt,
            goodsReceipt: actionState.goodsReceipt,
          })}
        />
      )}

      {actionState?.type === GoodReceiptActionType.Delete && (
        <GoodsReceiptDeleteConfirmation
          goodsReceipt={
            (actionState as { type: GoodReceiptActionType.Delete; goodsReceipt: GoodsReceipt }).goodsReceipt
          }
          onClose={handleDialogClose}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </Box>
  )
}
